import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Tag, Typography, Card, message, Row, Col, Input, Popconfirm, Tabs, Tooltip, Grid } from 'antd';
import { PlusOutlined, EyeOutlined, CheckCircleOutlined, SearchOutlined, CloseCircleOutlined, PrinterOutlined } from '@ant-design/icons';
import ExportButton from '../components/ExportButton';
import client from '../api/client';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import JournalVoucherModal from '../components/JournalVoucherModal';
import { useAuth } from '../context/AuthContext';

const { Title } = Typography;

const PurchasingPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { hasPermission, user } = useAuth();
    const isManagerOrAdmin = user?.role === 'ADMIN' || user?.role === 'MANAGER';
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('palay');
    const [voucherVisible, setVoucherVisible] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        fetchPurchases();
    }, [activeTab]);

    const fetchPurchases = async () => {
        setLoading(true);
        try {
            const { data } = await client.get('/transactions/purchases', {
                params: { commodity: activeTab }
            });
            setData(data);
        } catch (error) {
            message.error('Failed to fetch purchases');
        } finally {
            setLoading(false);
        }
    };

    const handlePost = async (id) => {
        try {
            await client.post(`/transactions/purchases/${id}/post`);
            message.success('Purchase posted successfully');
            fetchPurchases();
        } catch (error) {
            message.error(error.response?.data?.message || 'Posting failed');
        }
    };

    const handleVoid = async (id) => {
        try {
            await client.post(`/transactions/purchases/${id}/void`);
            message.success('Purchase voided successfully');
            fetchPurchases();
        } catch (error) {
            message.error(error.response?.data?.message || 'Voiding failed');
        }
    };

    const handleViewVoucher = async (record) => {
        setVoucherVisible(true);
        setSelectedPurchase(null);
        try {
            const { data } = await client.get(`/transactions/purchases/${record.id}`);
            setSelectedPurchase(data);
        } catch (error) {
            message.error('Failed to load journal voucher details');
        }
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'transaction_date',
            key: 'date',
            render: (date) => dayjs(date).format('MMM D, YYYY'),
        },
        {
            title: 'Ticket #',
            dataIndex: 'ticket_number',
            key: 'number',
        },
        {
            title: 'Partner / Supplier',
            dataIndex: ['partner', 'name'],
            key: 'partner',
        },
        {
            title: 'Item',
            key: 'items',
            render: (_, record) => record.item?.name || record.items?.[0]?.item?.name || 'N/A',
        },
        {
            title: 'Net Weight',
            dataIndex: 'net_weight',
            key: 'weight',
            align: 'right',
            render: (val) => `${parseFloat(val).toLocaleString()} kg`,
        },
        {
            title: 'Amount',
            dataIndex: 'total_amount',
            key: 'amount',
            align: 'right',
            render: (val) => `₱${parseFloat(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
        },
        {
            title: 'Balance',
            dataIndex: 'balance',
            key: 'balance',
            align: 'right',
            render: (val) => `₱${parseFloat(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
        },
        {
            title: 'Payment',
            dataIndex: 'payment_status',
            key: 'payment_status',
            render: (status) => {
                const colors = { UNPAID: 'red', PARTIAL: 'orange', PAID: 'green' };
                return <Tag color={colors[status] || 'default'}>{status || 'UNPAID'}</Tag>;
            }
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                let color = 'default';
                if (status === 'POSTED') color = 'success';
                if (status === 'DRAFT') color = 'warning';
                if (status === 'VOIDED') color = 'error';
                return <Tag color={color}>{status}</Tag>;
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space size="middle">
                    <Button icon={<EyeOutlined />} onClick={() => navigate(`/purchasing/${record.id}`)} />
                    {record.status === 'DRAFT' && hasPermission('post_transaction') && isManagerOrAdmin && (
                        <Button
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            onClick={() => handlePost(record.id)}
                        >
                            Post
                        </Button>
                    )}
                    {record.status === 'POSTED' && hasPermission('void_transaction') && (
                        <Popconfirm
                            title="Void this purchase?"
                            description="This will mark the purchase as VOIDED."
                            onConfirm={() => handleVoid(record.id)}
                            okText="Void"
                            cancelText="Cancel"
                        >
                            <Button type="default" danger icon={<CloseCircleOutlined />}>
                                Void
                            </Button>
                        </Popconfirm>
                    )}
                    {record.status === 'POSTED' && (
                        <Tooltip title="Print Journal Voucher">
                            <Button
                                icon={<PrinterOutlined />}
                                onClick={() => handleViewVoucher(record)}
                            />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    const filteredData = data.filter((item) => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return true;
        return (
            (item.ticket_number || '').toLowerCase().includes(term) ||
            (item.partner?.name || '').toLowerCase().includes(term) ||
            (item.status || '').toLowerCase().includes(term) ||
            (item.payment_status || '').toLowerCase().includes(term)
        );
    });

    const totalWeight = filteredData.reduce((sum, item) => sum + parseFloat(item.net_weight || 0), 0);
    const totalAmount = filteredData.reduce((sum, item) => sum + parseFloat(item.total_amount || 0), 0);
    const outstandingBalance = filteredData.reduce((sum, item) => sum + parseFloat(item.balance || 0), 0);
    const draftCount = filteredData.filter(item => item.status === 'DRAFT').length;

    const StatCard = ({ title, value, prefix, suffix, icon, color, subText }) => (
        <Card className="premium-card" bodyStyle={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <Typography.Text style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary, #64748b)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography.Text>
                    <Title level={3} style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>
                        {prefix}{parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}{suffix}
                    </Title>
                    {subText && <Typography.Text style={{ fontSize: 12, color: 'var(--text-secondary, #94a3b8)', marginTop: 4, display: 'block' }}>{subText}</Typography.Text>}
                </div>
                <div className="stat-icon" style={{ backgroundColor: `${color}15`, color: color }}>
                    {icon}
                </div>
            </div>
        </Card>
    );

    const tabItems = [
        {
            key: 'palay',
            label: 'Palay',
        },
        {
            key: 'rice',
            label: 'Rice',
        }
    ];

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                <div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800 }}>Purchasing</Title>
                    <Typography.Text style={{ fontSize: 16, color: 'var(--text-secondary, #64748b)' }}>Monitor procurement, payouts, and incoming stock.</Typography.Text>
                </div>
                <Space wrap>
                    <ExportButton
                        filename="purchases"
                        sheetName="Purchases"
                        title="Purchasing"
                        headers={['Ticket #', 'Date', 'Supplier', 'Item', 'Net Weight (kg)', 'Total Amount', 'Balance', 'Payment Status', 'Status']}
                        getData={() => filteredData.map(r => [
                            r.ticket_number,
                            dayjs(r.transaction_date).format('YYYY-MM-DD'),
                            r.partner?.name || '',
                            r.item?.name || r.items?.[0]?.item?.name || '',
                            r.net_weight,
                            r.total_amount,
                            r.balance,
                            r.payment_status || 'UNPAID',
                            r.status,
                        ])}
                        style={{ borderRadius: 8 }}
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        size="large"
                        onClick={() => navigate(`/purchasing/new?commodity=${activeTab}`)}
                        style={{ height: 45, borderRadius: 10, fontWeight: 600, padding: '0 24px', width: isMobile ? '100%' : 'auto' }}
                    >
                        New Purchase Receipt
                    </Button>
                </Space>
            </div>

            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                size="large"
                style={{ marginBottom: 24 }}
            />

            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                <Col xs={24} sm={12} lg={6}>
                    <StatCard
                        title="Total Volume"
                        value={totalWeight}
                        suffix=" kg"
                        icon={<i className="fa-solid fa-weight-hanging"></i>}
                        color="#3b82f6"
                        subText="Total weight purchased"
                    />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <StatCard
                        title="Total Purchases"
                        value={totalAmount}
                        prefix="₱"
                        icon={<i className="fa-solid fa-wallet"></i>}
                        color="#10b981"
                        subText="Total purchased amount"
                    />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <StatCard
                        title="Outstanding Payables"
                        value={outstandingBalance}
                        prefix="₱"
                        icon={<i className="fa-solid fa-file-invoice-dollar"></i>}
                        color="#ef4444"
                        subText="Open supplier balances"
                    />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <StatCard
                        title="Pending Posting"
                        value={draftCount}
                        icon={<i className="fa-solid fa-clock-rotate-left"></i>}
                        color="#f59e0b"
                        subText="Draft receipts"
                    />
                </Col>
            </Row>

            <Card className="premium-card" bodyStyle={{ padding: 24 }}>
                <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'flex-end', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0 }}>
                    <Input
                        placeholder="Search ticket #, partner, status..."
                        prefix={<SearchOutlined style={{ color: 'var(--text-secondary)' }} />}
                        style={{ width: isMobile ? '100%' : 340, borderRadius: 10, height: 40 }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <Table
                    columns={columns}
                    dataSource={filteredData}
                    rowKey="id"
                    loading={loading}
                    size={isMobile ? 'small' : 'middle'}
                    scroll={isMobile ? { x: 1100 } : undefined}
                    pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                />
            </Card>

            <JournalVoucherModal
                visible={voucherVisible}
                onCancel={() => setVoucherVisible(false)}
                data={selectedPurchase}
            />
        </div>
    );
};

export default PurchasingPage;




